import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Feedstock } from '../../../../../models/project/feedstock/feedstock.model';
import { MaterialService } from '../../../material.service';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { FeedstockDropdownsSearchComponent } from './feedstock-dropdowns-search/feedstock-dropdowns-search.component';
import { MaterialSearchData } from '../../../../../models/data-structures/materials/material-search-data.model';
import { ConfiguredToastrService } from '../../../../../helper/toastr/configured-toastr.service';
import { AnessaTranslationService } from '../../../../../helper/translation/anessa-translation.service';
import { FeedstockService } from '../../../feedstock.service';
import { DropdownDisplayItem } from '../../../../../models/data-structures/dropdown-display-item';
import { FeedstockBmpType } from '../../../../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { FeedstockMaterial } from '../../../../../models/data-structures/materials/materials-data.model';
import { UnitValue } from '../../../../../models/data-structures/unit-value.model';
import { FeedstockAutofillPackage } from '../../../../../models/project/feedstock/feedstock-autofill-package.model';
import { UserpilotReloader } from '../../../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-feedstock-dropdowns',
  templateUrl: './feedstock-dropdowns.component.html',
  styleUrls: ['./feedstock-dropdowns.component.scss']
})

/**
 * Contains all functionality for feedstock material dropdowns
 */
export class FeedstockDropdownsComponent extends UserpilotReloader implements OnInit, OnDestroy {
  /** The current feedstock instance */
  @Input() feedstock: Feedstock = null;
  /** A boolean to determine what elements to hide or display */
  @Input() projectInputDisplay = false;
  /**
   * A notifier that signifies that a feedstock has been loaded, or that the currently
   * loaded feedstock has changed
   */
  @Input() feedstockChangedNotifier = new EventEmitter<Feedstock>();
  /** A notifier that emits <null> when a change is made to the current feedstock */
  @Output() changesMadeNotifier = new EventEmitter();

  /*
    local variables
   */

  /** an notifier that will be triggered when this component is destroyed */
  onDestroyNotifier = new EventEmitter();
  /** an array of DropdownDisplayItems that contains all material categories */
  materialCategories: DropdownDisplayItem[] = [];
  /** an array of DropdownDisplayItems that contains all material types */
  materialTypes: DropdownDisplayItem[] = [];
  /** an array of DropdownDisplayItems that contains all materials sources */
  materialSources: DropdownDisplayItem[] = [];
  /** trigger for translation pipe */
  trigger: string;

  // clear button toggles
  showCatClear = false;
  showTypeClear = false;
  showSourceClear = false;

  // selected data
  selectedCategory: DropdownDisplayItem;
  selectedType: DropdownDisplayItem;
  selectedSource: DropdownDisplayItem;

  constructor(private materialService: MaterialService,
              private matDialog: MatDialog,
              private toastrService: ConfiguredToastrService,
              private feedstockService: FeedstockService,
              public translationService: AnessaTranslationService
  ) {
    super();
  }

  /**
   * sets up subscriptions to initialize material data and update listen for feedstock changes
   */
  ngOnInit() {
    this.setData();

    this.materialService.feedstockMaterialsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.materialCategories = this.materialService.getMaterialCategories();
          this.setData();
        }
      });

    this.translationService.languageChangeNotifier
    .pipe(takeUntil(this.onDestroyNotifier))
    .subscribe(() => {
      this.trigger = this.translationService.getCurrentLang();
    });

    this.feedstockChangedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(feedstock => {
        this.setData();
      });
  }

  /**
   * cleans up all subscriptions within the component; triggers onDestroyNotifier and unsubscribes from it
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Set the selected feedstock data based on the current feedstock.
   */
  setData(autofill = false) {
    if (!this.feedstock) {
      return;
    }

    // Load material from database id
    let selectedMaterial: FeedstockMaterial;
    if (this.feedstock.materialId) {
      selectedMaterial = this.materialService.getMaterialById(this.feedstock.materialId);
    }

    this.materialTypes = [];
    this.materialSources = [];

    // Set material category
    if (this.materialCategories) {
      if (selectedMaterial) {
        this.selectedCategory = this.materialCategories.find(cat => {
          return cat.value === selectedMaterial.category;
        });
      } else {
        this.selectedCategory = this.materialCategories.find(cat => {
          return cat.value === this.feedstock.category;
        });
      }
      // this is mainly for old feedstocks so they can be updated by just saving
      // but this will update the data with translation ID changes made to the categories on save.
      if (this.selectedCategory) {
        this.feedstock.category = this.selectedCategory.value;
        this.feedstock.catTranslationId = this.selectedCategory.translationId;
        this.materialTypes = this.materialService.getMaterialTypesForCat(this.selectedCategory.value);
      } else {
        this.feedstock.catTranslationId = null;
      }
    }
    // Set material type
    if (this.materialTypes) {
      if (selectedMaterial) {
        this.selectedType = this.materialTypes.find(type => {
          return type.value === selectedMaterial.type;
        });
      } else {
        this.selectedType = this.materialTypes.find(type => {
          return type.value === this.feedstock.type;
        });
      }
      if (this.selectedCategory && this.selectedType) {
        this.feedstock.type = this.selectedType.value;
        this.feedstock.typeTranslationId = this.selectedType.translationId;
        this.materialSources = this.materialService.getMaterialSourcesForType(
          this.selectedCategory.value,
          this.selectedType.value,
        );
      } else {
        this.feedstock.typeTranslationId = null;
      }
    }
    // Set material source
    if (this.materialSources) {
      if (selectedMaterial) {
        this.selectedSource = this.materialSources.find(source => {
          return source.value === selectedMaterial.source;
        });
      } else {
        this.selectedSource = this.materialSources.find(source => {
          return source.value === this.feedstock.source;
        });
      }
      if (this.selectedSource) {
        this.feedstock.source = this.selectedSource.value;
        this.feedstock.sourceTranslationId = this.selectedSource.translationId;
      } else {
        this.feedstock.sourceTranslationId = null;
      }
    }

    // Autofill data
    if (!this.projectInputDisplay && autofill) {
      if (!selectedMaterial) {
        this.feedstockService.materialSelectedNotifier.next(null);
      }

      const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
        data: {
          message: this.translationService.get('PROJECT.FEEDSTOCK.TYPE.DB_AUTOFILL'),
          confirmText: 'PROJECT.COMMON.YES',
          rejectText: 'PROJECT.COMMON.NO',
        }
      });

      dialogRef.afterClosed().toPromise().then((confirm: boolean) => {
        if (confirm) {
          const loadedMaterial = new FeedstockMaterial(selectedMaterial);
          this.populateFeedstockData(loadedMaterial);
          this.feedstockService.materialSelectedNotifier.next(loadedMaterial);
        }
      });

    }
  }

  /**
   * Sets category data and handles data processing that happens when the feedstock category is changed.
   * @param event The currently selected dropdown display item.
   */
  onCategoryChange(event: DropdownDisplayItem) {
    if (!this.feedstock) {
      throw Error('failed to set feedstock category');
    }
    this.feedstock.category = event.value;
    this.feedstock.catTranslationId = event.translationId;

    this.selectedType = null;
    this.materialTypes = [];
    this.selectedSource = null;
    this.materialSources = [];
    this.cleanUpData();

    if (this.feedstock.category === 'unknown') {
      this.materialTypes = [FeedstockMaterial.getUnknownDisplayItem()];
      this.materialSources = [FeedstockMaterial.getUnknownDisplayItem()];
      this.selectedType = this.materialTypes[0];
      this.selectedSource = this.materialSources[0];
    }

    this.changesMadeNotifier.emit();

  }

  /**
   * Sets type data and handles data processing that happens when the feedstock type is changed.
   * @param event The currently selected dropdown display item.
   */
  onTypeChange(event: DropdownDisplayItem) {
    if (!this.feedstock) {
      throw Error('failed to set feedstock category');
    }
    this.feedstock.type = event.value;
    this.feedstock.typeTranslationId = event.translationId;

    this.selectedSource = null;
    this.materialSources = [];

    /* the source is null and first material matched with category and type will be used */
    const material = this.materialService.getMaterialByValues(
      this.selectedCategory.value,
      this.selectedType.value,
      null
    );
    if (material) {
      this.feedstock.materialId = material.id;
      this.setIsAnimal();
    }

    this.cleanUpData();
    this.changesMadeNotifier.emit();
  }

  /**
   * Sets source data and handles data processing that happens when the feedstock source is changed.
   * @param event The currently selected dropdown display item.
   */
  onSourceChange(event: DropdownDisplayItem) {
    if (!this.feedstock) {
      throw Error('failed to set feedstock category');
    }

    this.feedstock.source = event.value;
    this.feedstock.sourceTranslationId = event.translationId;

    const material = this.materialService.getMaterialByValues(
      this.selectedCategory.value,
      this.selectedType.value,
      this.selectedSource.value,
    );
    if (material) {
      this.feedstock.materialId = material.id;
      this.setIsAnimal();
    }

    this.cleanUpData(true);

    this.changesMadeNotifier.emit();
  }

  /**
   * sets the current feedstocks isAnimals member depending on the currently selected material.
   */
  private setIsAnimal() {
    if (!this.feedstock || !this.feedstock.quantities) {
      throw Error(this.translationService.get('PROJECT.FEEDSTOCK.TYPE.INVALID'));
    }

    const material = this.materialService.getMaterialById(this.feedstock.materialId);
    if (material) {
      // this.feedstock.quantities.isAnimal = this.feedstock.quantities.isAnimal ?? material.isAnimal;
      // if (this.feedstock.quantities.isAnimal == false && material.isAnimal) {
      //   this.feedstock.quantities.isAnimal = false;
      // } else {
      //   this.feedstock.quantities.isAnimal =  material.isAnimal;
      // }
      if ((this.feedstock.quantities.isAnimal ?? true) || !(material.isAnimal ?? false)) {
        this.feedstock.quantities.isAnimal =  material.isAnimal;
      }
    } else {
      this.feedstock.quantities.isAnimal = false;
    }
  }

  /**
   * Opens the material search pop-up and handles the data it returns
   */
  onSearchClick() {
    const dialog =  this.matDialog.open(
      FeedstockDropdownsSearchComponent,
      {
        width: '50vw',
        height: '62vh'
      }
    );

    dialog.afterClosed().pipe(take(1)).subscribe(res => {
      if (res && res instanceof MaterialSearchData) {
        if (res.isValid()) {
          this.feedstock.materialId = res.id;
          this.feedstock.category = res.category;
          this.feedstock.type = res.type;
          this.feedstock.source = res.source;
          this.setData(true);
          this.changesMadeNotifier.emit();
          this.setIsAnimal();
          if (!this.projectInputDisplay) {
            this.toastrService.info(this.translationService.get('PROJECT.FEEDSTOCK.TYPE.SRC_CHANGE'), '', 6);
          }
        }
      }
    });
  }

  /**
   * Clears the provided property from the current feedstock object.
   * @param property is the property name of the target property that will be cleared.
   */
  onClear(property: string) {
    switch (property) {
      case 'category':
        this.selectedCategory = null;
        break;
      case 'type':
        this.selectedType = null;
        break;
      case 'source':
        this.selectedSource = null;
        break;
    }
    this.cleanUpData();
    this.changesMadeNotifier.emit();
  }

  /**
   * Removes the type and sources depending on what data is selected. If a category
   * isn't selected, then both type and source should not be selected. If a type is not selected
   * but a category is, then just the source will be cleared.
   */
  private cleanUpData(autofill = false) {
    if (!this.selectedCategory) {
      this.selectedType = null;
      this.feedstock.category = null;
    }
    if (!this.selectedType) {
      this.selectedSource = null;
      this.feedstock.type = null;
    }
    if (!this.selectedSource) {
      this.feedstock.source = null;
      this.feedstock.materialId = null;
    }
    this.setData(autofill);
  }

  private populateFeedstockData(loadedMaterial: FeedstockMaterial) {
    const autofillPackage = new FeedstockAutofillPackage();
    // ts
    this.feedstock.characteristics.ts = new UnitValue(loadedMaterial.totalSolids);
    autofillPackage.characteristicsPkg.ts = new UnitValue(loadedMaterial.totalSolids);
    // vs
    this.feedstock.characteristics.vs = new UnitValue(loadedMaterial.volatileSolids);
    autofillPackage.characteristicsPkg.vs = new UnitValue(loadedMaterial.volatileSolids);
    // bmp type
    this.feedstock.bmpPotential.type = FeedstockBmpType.BMP_DIRECT;
    // bmp
    this.feedstock.bmpPotential.bmpDirect.bmp = new UnitValue(loadedMaterial.biochemMethanePot);
    autofillPackage.bmpPkg.bmp = new UnitValue(loadedMaterial.biochemMethanePot);
    // ch4 concentration
    // Set methane concentration to 60% if custom value not present
    if (loadedMaterial.methaneConcentration == null || loadedMaterial.methaneConcentration.value == null) {
      this.feedstock.bmpPotential.bmpDirect.ch4Concentration = new UnitValue({ unit: '000000000000000000000401', value: 60 });
      autofillPackage.bmpPkg.ch4Concentration = new UnitValue({ unit: '000000000000000000000401', value: 60 });
      loadedMaterial.methaneConcentration = new UnitValue({ unit: '000000000000000000000401', value: 60 });
    } else {
      this.feedstock.bmpPotential.bmpDirect.ch4Concentration = new UnitValue(loadedMaterial.methaneConcentration);
      autofillPackage.bmpPkg.ch4Concentration = new UnitValue(loadedMaterial.methaneConcentration);
    }
    // period
    // Set duration of test to 30 days
    this.feedstock.bmpPotential.bmpDirect.period = new UnitValue({ unit: '000000000000000000001302', value: 30 });
    autofillPackage.bmpPkg.period = new UnitValue({ unit: '000000000000000000001302', value: 30 });
    this.feedstock.autofillPackage = autofillPackage;
  }
}
